import { useEffect } from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { Layout } from '@/components/layout'

import { assertExhaustive } from '@/utils/assert'

import { useAuthSync } from '@/entities/user/api/firebase/subscriptions'

import { ConnectionChecker } from '@/features/connection-checker'

import {
  startRundownSession,
  stopRundownSession,
  useRundownSession,
} from './rundown-session'
import { useEnsurePrivateNotesColumn } from './rundown-session.private-column'
import { useRundownTiming } from './rundown-session.timing'
import { RundownRouteError } from './RundownRouteError'

/**
 * Route component for `/rundown/:rundownId`.
 *
 * Owns the rundown session lifecycle and gates the child routes (`rundown`,
 * `rundown-output`) on readiness.
 *
 * All data is loaded in `rundown-session.ts`, this component only renders UI.
 */
export function RundownRoute() {
  const { rundownId } = useParams()

  if (!rundownId) throw new Error('RundownRoute rendered without :rundownId')

  useAuthSync()

  // useEffect: Firestore subscriptions are external side effects that must
  // start on mount and stop on unmount or rundown change.
  useEffect(() => {
    startRundownSession(rundownId)

    return () => stopRundownSession()
  }, [rundownId])

  const session = useRundownSession()

  switch (session.status) {
    case 'idle':
    case 'loading': {
      return (
        <Layout
          title="Loading..."
          loading
        />
      )
    }

    case 'error': {
      if (!session.error) {
        throw new Error('Rundown session in error state without an error')
      }
      return <RundownRouteError error={session.error} />
    }

    case 'ready': {
      return <RundownReady rundownId={rundownId} />
    }

    default: {
      return assertExhaustive(session.status)
    }
  }
}

function RundownReady({ rundownId }: { rundownId: string }) {
  useRundownTiming()
  useEnsurePrivateNotesColumn(rundownId)

  return (
    <>
      {/* Render child routes */}
      <Outlet />
      {/* Render connection status */}
      <ConnectionChecker />
    </>
  )
}
